import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { Modal } from '../ui/Modal.tsx';
import { useTranslation } from '../../i18n.ts';

/* ────────────────────────────────────────────────────── */
/*  Types                                                 */
/* ────────────────────────────────────────────────────── */

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  variant?: 'destructive' | 'default';
}

interface PromptOptions {
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

/** Eén knop in een meerkeuze-dialog (zie ChoiceOptions / useChoice). */
interface ChoiceButton {
  /** Waarde die `choose()` resolvet als deze knop wordt gekozen. */
  value: string;
  label: string;
  variant?: 'primary' | 'neutral' | 'danger';
}

interface ChoiceOptions {
  title: string;
  message?: string;
  /** Knoppen, links→rechts. De `primary`-knop krijgt focus. */
  buttons: ChoiceButton[];
  /** Resolve-waarde bij X/Escape/backdrop (default: 'cancel'). */
  dismissValue?: string;
}

interface ModalState {
  type: 'confirm' | 'prompt' | 'choice';
  options: ConfirmOptions | PromptOptions | ChoiceOptions;
  resolve: (value: boolean | string | null) => void;
}

interface ModalContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
  /** Meerkeuze-dialog (bv. Opslaan/Verwerpen/Annuleren). Resolvet de gekozen value. */
  choose: (options: ChoiceOptions) => Promise<string>;
}

/* ────────────────────────────────────────────────────── */
/*  Context                                               */
/* ────────────────────────────────────────────────────── */

const ModalContext = createContext<ModalContextType | null>(null);

export const useConfirm = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useConfirm must be used within ModalProvider');
  return ctx.confirm;
};

export const usePrompt = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('usePrompt must be used within ModalProvider');
  return ctx.prompt;
};

export const useChoice = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useChoice must be used within ModalProvider');
  return ctx.choose;
};

/* ────────────────────────────────────────────────────── */
/*  Provider                                              */
/* ────────────────────────────────────────────────────── */

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<ModalState | null>(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setModal({
        type: 'confirm',
        options,
        resolve: resolve as (value: boolean | string | null) => void,
      });
    });
  }, []);

  const prompt = useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setInputValue((options as PromptOptions).defaultValue || '');
      setModal({
        type: 'prompt',
        options,
        resolve: resolve as (value: boolean | string | null) => void,
      });
    });
  }, []);

  const choose = useCallback((options: ChoiceOptions): Promise<string> => {
    return new Promise((resolve) => {
      setModal({
        type: 'choice',
        options,
        resolve: resolve as (value: boolean | string | null) => void,
      });
    });
  }, []);

  const handleClose = useCallback(
    (value: boolean | string | null) => {
      modal?.resolve(value);
      setModal(null);
      setInputValue('');
    },
    [modal]
  );

  // Focus management
  useEffect(() => {
    if (!modal) return;
    requestAnimationFrame(() => {
      if (modal.type === 'prompt') inputRef.current?.focus();
      else confirmBtnRef.current?.focus();
    });
  }, [modal]);

  const isConfirm = modal?.type === 'confirm';
  const isChoice = modal?.type === 'choice';
  const opts = modal?.options;
  const destructive = isConfirm && (opts as ConfirmOptions)?.destructive;
  const dialogMessage = isChoice
    ? (opts as ChoiceOptions)?.message
    : isConfirm
      ? (opts as ConfirmOptions)?.message
      : (opts as PromptOptions)?.message;

  // X / backdrop / Escape: confirm→false, prompt→null, choice→dismissValue ('cancel').
  // De veilige default voor de sluit-bevestiging is dus "blijf in de editor".
  const handleModalClose = useCallback(() => {
    if (modal?.type === 'choice') {
      handleClose((modal.options as ChoiceOptions).dismissValue ?? 'cancel');
    } else {
      handleClose(isConfirm ? false : null);
    }
  }, [handleClose, isConfirm, modal]);

  const choiceBtnClass = (variant?: ChoiceButton['variant']): string => {
    if (variant === 'neutral') {
      return 'px-5 py-2.5 text-xs font-black uppercase tracking-widest text-pt-sage-text dark:text-[var(--text-muted)] hover:text-pt-forest dark:hover:text-[var(--text-main)] transition-colors rounded-xl hover:bg-pt-forest/5 dark:hover:bg-white/5';
    }
    if (variant === 'danger') {
      return 'px-6 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg bg-pt-ruby text-white hover:bg-pt-ruby-hover';
    }
    return 'px-6 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg bg-pt-forest text-pt-gold hover:bg-[#122219]';
  };

  return (
    <ModalContext.Provider value={{ confirm, prompt, choose }}>
      {children}
      <Modal isOpen={!!modal} onClose={handleModalClose} titleId="modal-title-confirm" size="sm">
        {modal && (
          <>
            {/* Header */}
            <div className="p-6 pb-0 flex items-start gap-4">
              <div
                className={`p-3 rounded-2xl shrink-0 ${destructive ? 'bg-pt-ruby/10 dark:bg-pt-ruby-light/10 text-pt-ruby dark:text-pt-ruby-light' : 'bg-pt-forest/5 dark:bg-white/5 text-pt-forest dark:text-[var(--text-main)]'}`}
              >
                {destructive ? <Trash2 size={20} /> : <AlertTriangle size={20} />}
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  id="modal-title-confirm"
                  className="text-lg font-serif font-bold text-pt-forest dark:text-[var(--text-main)]"
                >
                  {opts?.title}
                </h3>
                {dialogMessage && (
                  <p className="text-sm text-pt-sage-text dark:text-[var(--text-muted)] mt-1 leading-relaxed whitespace-pre-line">
                    {dialogMessage}
                  </p>
                )}
              </div>
              <button
                onClick={handleModalClose}
                className="flex items-center justify-center p-1.5 hover:bg-pt-forest/5 dark:hover:bg-white/5 rounded-full text-pt-sage-text dark:text-[var(--text-muted)] transition-colors shrink-0"
                aria-label={t('close')}
              >
                <X size={18} />
              </button>
            </div>

            {/* Input (prompt only) */}
            {modal.type === 'prompt' && (
              <div className="px-6 pt-4">
                <input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && inputValue.trim()) handleClose(inputValue.trim());
                  }}
                  placeholder={(opts as PromptOptions).placeholder || ''}
                  className="w-full bg-white dark:bg-[var(--bg-surface)] border border-pt-forest/10 dark:border-white/10 rounded-2xl px-5 py-3 text-sm font-bold text-pt-forest dark:text-[var(--text-main)] outline-none focus:ring-4 focus:ring-pt-gold/10 transition-all"
                  autoComplete="off"
                />
              </div>
            )}

            {/* Actions */}
            {isChoice ? (
              <div className="p-6 flex flex-wrap justify-end gap-3">
                {(opts as ChoiceOptions).buttons.map((btn) => (
                  <button
                    key={btn.value}
                    ref={btn.variant === 'primary' ? confirmBtnRef : undefined}
                    data-autofocus={btn.variant === 'primary' ? '' : undefined}
                    onClick={() => handleClose(btn.value)}
                    className={choiceBtnClass(btn.variant)}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-6 flex justify-end gap-3">
                <button
                  onClick={() => handleClose(isConfirm ? false : null)}
                  className="px-5 py-2.5 text-xs font-black uppercase tracking-widest text-pt-sage-text dark:text-[var(--text-muted)] hover:text-pt-forest dark:hover:text-[var(--text-main)] transition-colors rounded-xl hover:bg-pt-forest/5 dark:hover:bg-white/5"
                >
                  {(opts as ConfirmOptions | PromptOptions)?.cancelLabel || t('cancel')}
                </button>
                <button
                  ref={confirmBtnRef}
                  onClick={() => {
                    if (modal.type === 'confirm') handleClose(true);
                    else if (inputValue.trim()) handleClose(inputValue.trim());
                  }}
                  disabled={modal.type === 'prompt' && !inputValue.trim()}
                  className={`px-6 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg disabled:opacity-40 disabled:cursor-not-allowed ${
                    destructive
                      ? 'bg-pt-ruby text-white hover:bg-pt-ruby-hover'
                      : 'bg-pt-forest text-pt-gold hover:bg-[#122219]'
                  }`}
                >
                  {(opts as ConfirmOptions | PromptOptions)?.confirmLabel ||
                    (destructive ? t('delete') : t('confirm'))}
                </button>
              </div>
            )}
          </>
        )}
      </Modal>
    </ModalContext.Provider>
  );
};
